import React from "react";
import ProductsSelection from "./productsSelection";
import { Dialog, DialogContent } from "../ui/dialog";
import { ButtonPrimary } from "../common/ButtonPrimary";
import { StandardButton } from "../common/StandardButton";
import { useDiscountUpdate } from "@/hooks/useDiscountUpdate";
import { PageType, SelectedProduct } from "@/types/coupon/discount";
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "../ui/accordion";
import { generateImageSrc } from "@/utils/helper";
import { Package, X } from "lucide-react";
import { ApplicableProductsKeyType } from "@/types/coupon/discount";

type SelectedProductWithPage = SelectedProduct & { page?: PageType };

interface SelectedProductsListProps {
  currentPage: PageType;
  productsList: SelectedProduct[];
  onRemove?: (id: string) => void;
}

const SelectedProductsList = ({
  currentPage,
  productsList = [],
  onRemove,
}: SelectedProductsListProps) => {
  const [editOpen, setEditOpen] = React.useState(false);
  const [editPrefill, setEditPrefill] = React.useState<SelectedProduct[]>([]);
  const [editPage, setEditPage] = React.useState<PageType>(currentPage);
  const handleUpdate = useDiscountUpdate();

  const selectedIds = new Set(editPrefill.map((p) => p.id));
  const productsListWithPage: SelectedProductWithPage[] = productsList
    .map((product) => ({
      ...product,
      page: (product as any).page ?? currentPage,
    }))
    .sort((a, b) => {
      const aSelected = selectedIds.has(a.id);
      const bSelected = selectedIds.has(b.id);
      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;
      return 0;
    });

  const removeProduct = (id: string) => {
    handleUpdate(currentPage, {
      applicable_products: productsList.filter((p) => p.id !== id),
    });
    if (onRemove) {
      onRemove(id);
    }
  };

  const handleEdit = (productPage?: PageType) => {
    setEditPrefill(productsList);
    setEditPage(productPage || currentPage);
    setEditOpen(true);
  };

  const handleEditClose = () => {
    setEditOpen(false);
  };

  return (
    <>
      <div className="space-y-2">
        <Accordion type="multiple" className="space-y-2">
          {productsListWithPage.map(({ id, title, totalVariants, variants, images, page }) => {
            const productImage = Array.isArray(images) && images.length > 0 
              ? generateImageSrc(images[0]) 
              : null;

            return (
              <AccordionItem 
                key={id} 
                value={id}
                className="border border-border rounded-lg overflow-hidden bg-background hover:border-primary/30 transition-colors"
              >
                <div className="flex items-center gap-2 px-3 py-2">
                  {/* Product Image */}
                  <div className="shrink-0 w-12 h-12 rounded-md overflow-hidden bg-muted">
                    {productImage ? (
                      <img
                        src={productImage}
                        alt={title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="h-5 w-5 text-muted-foreground" />
                      </div>
                    )}
                  </div>

                  {/* Product Info + Accordion Trigger */}
                  <AccordionTrigger className="flex-1 hover:no-underline py-0 min-w-0">
                    <div className="flex-1 min-w-0 text-left mr-2">
                      <p className="text-sm font-semibold text-foreground truncate">
                        {title}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        {variants.length} of {totalVariants} variants
                      </p>
                    </div>
                  </AccordionTrigger>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-1.5 shrink-0">
                    <ButtonPrimary
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEdit(page);
                      }}
                      className="h-8 px-3 text-xs"
                    >
                      Edit
                    </ButtonPrimary>
                    <StandardButton
                      onClick={(e) => {
                        e.stopPropagation();
                        removeProduct(id);
                      }}
                      variant="destructive"
                      className="h-8 w-8 p-0"
                    >
                      <X className="h-4 w-4" />
                    </StandardButton>
                  </div>
                </div>

                {/* Variants List */}
                <AccordionContent className="px-3 pb-2">
                  <div className="space-y-1.5 mt-1">
                    {variants.map((variant) => {
                      const variantImage = variant.image 
                        ? generateImageSrc(variant.image) 
                        : null;
                      
                      // Format price from smallest currency unit (e.g., 100000 = ₹1000.00)
                      const formattedPrice = variant.amount 
                        ? `₹${(variant.amount / 100).toFixed(2)}`
                        : null;

                      return (
                        <div
                          key={variant.id}
                          className="flex items-center gap-2 p-2 rounded-md bg-muted/40 hover:bg-muted/60 transition-colors"
                        >
                          {/* Variant Image */}
                          <div className="shrink-0 w-10 h-10 rounded overflow-hidden bg-muted border border-border/50">
                            {variantImage ? (
                              <img
                                src={variantImage}
                                alt={variant.title || "Variant"}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <Package className="h-4 w-4 text-muted-foreground" />
                              </div>
                            )}
                          </div>

                          {/* Variant Info */}
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-foreground truncate">
                              {variant.title && variant.title.trim() !== "" ? variant.title : "Default Variant"}
                            </p>
                            <div className="flex items-center gap-3 mt-0.5 flex-wrap">
                              {variant.sku && (
                                <span className="text-[10px] text-muted-foreground font-mono">
                                  {variant.sku}
                                </span>
                              )}
                              {formattedPrice && (
                                <span className="text-[11px] font-semibold text-primary">
                                  {formattedPrice}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      </div>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="w-full max-h-3/4 min-w-1/2 !max-w-3/4 rounded-xl bg-secondary-background [&>button:last-child]:hidden p-6 pt-4 flex flex-col">
          <div className="flex justify-between mb-2">
            <span className="font-semibold text-lg">
              Edit Product Selection
            </span>
            <div className="flex items-center gap-3">
              <StandardButton
                variant="secondary"
                onClick={handleEditClose}
              >
                Close
              </StandardButton>
            </div>
          </div>
          <div className="space-y-6 flex flex-col overflow-hidden mt-2">
            <ProductsSelection
              currentPage={editPage}
              onClose={handleEditClose}
              applicableProductsKey={
                "applicable_products" as ApplicableProductsKeyType
              }
              searchValue={""}
              prefillSelected={editPrefill}
              initialTab="selected"
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default SelectedProductsList;
